import Pagination from "@mui/material/Pagination";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useMatch, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { usePopupStore, useTenantInMarketStore, useViewSettingsStore } from "../..";
import useActivePage from "../../hooks/useActivePage";
import useNavigateToTenantInMarket from "../../hooks/useNavigateToTenantInMarket";
import usePagination from "../../hooks/usePagination";
import useTenantsInMarket, { TenantsInMarketSortingType } from "../../hooks/useTenantsInMarket";
import useTenantsInMarketOwnerFilterValues from "../../hooks/useTenantsInMarketOwnerFilterValues";
import useTenantsInMarketWithFocusArea from "../../hooks/useTenantsInMarketWithFocusArea";
import PopupType from "../../interfaces/PopupType";
import useTenantsInMarketWithGlobalFilters from "../../interfaces/useTenantsInMarketWithGlobalFilters";
import { GlobalFiltersTab } from "../../store/FilterSettingsStore";
import { formatNumber } from "../../utils/utils";
import GlobalFiltersIcon from "../icons/GlobalFiltersIcon";
import SearchIcon from "../icons/SearchIcon";
import tabStyles from "../List.module.css";
import Spinner from "../spinners/Spinner";
import TenantInMarketInfo from "./TenantInMarketInfo";

function TenantsInMarket() {
    const { id } = useParams();
    const isTenantInMarketPage = useMatch("/tenantsInMarket/*");
    const tenantInMarketStore = useTenantInMarketStore();
    const popupStore = usePopupStore();
    const viewSettingsStore = useViewSettingsStore();
    const navigateToTenantInMarket = useNavigateToTenantInMarket();
    const ownerFilterValues = useTenantsInMarketOwnerFilterValues();
    const [ownerFilter, setOwnerFilter] = useState(ownerFilterValues[0]);
    const [searchFilter, setSearchFilter] = useState("");
    const [debouncedSearchFilter] = useDebounce(searchFilter, 500);
    const [sortBy, setSortBy] = useState(TenantsInMarketSortingType.ModifiedNewest);
    const [page, setPage] = useState(1);
    const tenantsInMarket = useTenantsInMarketWithFocusArea(useTenantsInMarketWithGlobalFilters(useTenantsInMarket(ownerFilter, debouncedSearchFilter, sortBy)));
    const [pageCount, pagedTenantsInMarket] = usePagination(tenantsInMarket, page, () => setPage(1));
    useActivePage(tenantsInMarket, Number(id), isTenantInMarketPage && id && tenantsInMarket.find(tim => tim.id === Number(id)), searchFilter, ownerFilter, sortBy, setPage);

    useEffect(() => {
        if (!ownerFilterValues.includes(ownerFilter))
            setOwnerFilter(ownerFilterValues[0]);
    }, [ownerFilterValues, ownerFilter]);

    return (
        <div className={tabStyles.container}>
            <div className={tabStyles.header}>
                {!tenantInMarketStore.isTenantsInMarketLoading.fetching && formatNumber(tenantsInMarket.length)} Tenants in Market
                {!viewSettingsStore.isMobile && <GlobalFiltersIcon tab={GlobalFiltersTab.TIMs} className={tabStyles.headerButton} />}
            </div>
            <div className={tabStyles.filters}>
                <div className={tabStyles.search}>
                    <SearchIcon />
                    <input autoFocus={!viewSettingsStore.isMobile} placeholder="Search tenants in market.." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
                </div>
                <div className={tabStyles.dropdowns}>
                    <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
                        {ownerFilterValues.map(v => <option key={v}>{v}</option>)}
                    </select>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value={TenantsInMarketSortingType.ModifiedNewest}>Modified (Newest)</option>
                        <option value={TenantsInMarketSortingType.ModifiedOldest}>Modified (Oldest)</option>
                    </select>
                </div>
            </div>
            <div className={tabStyles.list}>
                {tenantInMarketStore.isTenantsInMarketLoading.fetching ? (
                    <Spinner className={tabStyles.spinner} />
                ) : (
                    pagedTenantsInMarket.map(tim => (
                        <TenantInMarketInfo
                            key={tim.id}
                            className={[
                                tabStyles.item,
                                isTenantInMarketPage && id === tim.id.toString()
                                    ? tabStyles.opened
                                    : popupStore.type === PopupType.TenantInMarket && popupStore.id === tim.id
                                        ? tabStyles.selected
                                        : "",
                            ].join(" ")}
                            description={tim.description}
                            landAreaMin={tim.landAreaMin}
                            landAreaMax={tim.landAreaMax}
                            buildingAreaMin={tim.buildingAreaMin}
                            buildingAreaMax={tim.buildingAreaMax}
                            onClick={() => navigateToTenantInMarket(tim.id)}
                        />
                    ))
                )}
            </div>
            <div className={tabStyles.pagination}>
                <Pagination count={pageCount} page={page} onChange={(_, page) => setPage(page)} size="small" />
            </div>
        </div>
    )
}

export default observer(TenantsInMarket);